import { useEffect, useState, useCallback, useRef } from "react";
import {
  Box,
  Button,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  LinearProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../Redux/Store/Hooks";
import { addArithmeticGameSkill } from "../../Redux/Slices/ArithmeticGameSkill/AddArithmeticGameSkillSlice";
import { ISkill } from "./ISkill";
import "./Arithmeticgame.css";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import Target from "./GamepageImage/target.svg";
import Trophy from "./GamepageImage/trophy-01.svg";
import Spark from "./GamepageImage/lightning.svg";
import Tick from "./GamepageImage/check-circle.svg";

const ArithmeticGame = () => {
  const dispatch = useAppDispatch();
  const nav = useNavigate();
  const { data: addArithmeticGame } = useAppSelector(
    (state) => state.AddArithmeticGameSkillSlice
  );

  const Data = addArithmeticGame?.data as ISkill[] | undefined;

  const [timeLeft, setTimeLeft] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [paused, setPaused] = useState(false);
  const [score, setScore] = useState(0);
  const [answer, setAnswer] = useState("");
  const [currentProblem, setCurrentProblem] = useState<ISkill | null>(null);
  const [problemsAttempted, setProblemsAttempted] = useState(0);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);

  const inputRef = useRef<HTMLInputElement>(null);
  const getOperatorSymbol = (operation?: string): string => {
    switch (operation) {
      case "add":
      case "addition":
        return "+";
      case "sub":
      case "subtract":
        return "−";
      case "mul":
      case "multiply":
        return "×";
      case "div":
      case "divide":
        return "÷";
      default:
        return "?";
    }
  };

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    const settings = sessionStorage.getItem("arithmeticGameSettings");
    const storedTime = sessionStorage.getItem("timergame");

    if (!settings) {
      nav("/skillstrainer");
      return;
    }

    setTimeLeft(storedTime ? Number(storedTime) : 30);
    const parsedSettings = JSON.parse(settings);
    dispatch(addArithmeticGameSkill(parsedSettings));
  }, [dispatch, nav]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  useEffect(() => {
    if (gameStarted && timeLeft > 0 && !gameOver && !paused) {
      const timer = setInterval(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    }

    if (timeLeft === 0 && !gameOver && gameStarted) {
      setGameOver(true);
    }
  }, [timeLeft, gameStarted, gameOver, paused]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter") handleSubmitAnswer();
      if (e.key === "Escape") setPaused(true);
      if (e.key.toLowerCase() === "r") handleRetry();
    };
    if (answer.trim()) {
      window.addEventListener("keydown", handleKeyDown);
    }
    return () => window.removeEventListener("keydown", handleKeyDown);
  });

  const generateNewProblem = useCallback(() => {
    const settings = sessionStorage.getItem("arithmeticGameSettings");
    if (settings) {
      const parsedSettings = JSON.parse(settings);
      dispatch(addArithmeticGameSkill(parsedSettings));
    }
  }, [dispatch]);

  const handleAnswerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAnswer(e.target.value);
  };

  const handleSubmitAnswer = () => {
    if (
      !currentProblem ||
      gameOver ||
      typeof currentProblem.answer !== "number"
    )
      return;

    const userAnswer = parseFloat(answer);
    const correctAnswer = currentProblem.answer;

    // ✅ Works for negative answers too
    const isCorrect = Math.abs(userAnswer - correctAnswer) < 0.0001;

    setProblemsAttempted((prev) => prev + 1);

    if (isCorrect) {
      setScore((prev) => prev + 12);
      setStreak((prev) => {
        const newStreak = prev + 1;
        if (newStreak > bestStreak) setBestStreak(newStreak);
        return newStreak;
      });
    } else {
      setStreak(0);
    }

    setAnswer("");
    generateNewProblem();
  };

  const handleRetry = () => {
    const storedTime = sessionStorage.getItem("timergame");
    setTimeLeft(storedTime ? Number(storedTime) : 30);
    setScore(0);
    setAnswer("");
    setGameOver(false);
    setGameStarted(true);
    setProblemsAttempted(0);
    setStreak(0);
    setBestStreak(0);
    setPaused(false);
    generateNewProblem();
  };

  const handleResume = () => setPaused(false);
  const handlePause = () => setPaused(true);
  const handleBack = () => nav("/skillstrainer");

  useEffect(() => {
    if (Data && Data.length > 0 && !gameOver) {
      setCurrentProblem(Data[0]);
      if (!gameStarted) setGameStarted(true);
    }
  }, [Data, gameOver, gameStarted]);

  const getAccuracy = () =>
    problemsAttempted > 0
      ? Math.round((score / 12 / problemsAttempted) * 100)
      : 0;

  return (
    <Box
      sx={{
        padding: "20px 0px",
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Box sx={{ maxWidth: "896px", width: "100%" }}>
        <Box
          className="arithmetic-header"
          mb={2}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            width: "100%",
          }}
        >
          {/* Left: Back Button */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "flex-start",
              minWidth: { xs: "80px", sm: "120px" }, // responsive min width
            }}
          >
            <Button
              onClick={handleBack}
              sx={{
                fontSize: { xs: "14px", sm: "16px" }, // shrink font on mobile
                color: "#36393ee9",
                textTransform: "none",
                fontWeight: 500,
                gap: "5px",
                marginLeft: "-5px",
                "& .MuiSvgIcon-root": { fontSize: "20px" },
              }}
            >
              <ArrowBackIcon />
              <span className="mobile_vw_hid">Back</span>
            </Button>
          </Box>

          {/* Center: Titles */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minWidth: { xs: "120px", sm: "200px" },
              textAlign: "center",
            }}
          >
            <Typography
              className="arithmetic-title"
              sx={{ fontSize: { xs: "16px", sm: "20px" }, fontWeight: 600 }}
            >
              Arithmetic Training
            </Typography>
            <Typography
              className="arithmetic-subtitle"
              sx={{
                fontSize: { xs: "12px", sm: "14px" },
                color: "text.secondary",
              }}
            >
              Addition Practice
            </Typography>
          </Box>

          {/* Right: Timer & Controls */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "flex-end",
              minWidth: { xs: "80px", sm: "150px" },
            }}
          >
            {!gameOver ? (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="flex-end"
                gap={1}
                sx={{ flexWrap: "wrap" }} // handles very small screens
              >
                <Typography
                  className={`timer ${timeLeft <= 10 ? "red" : ""}`}
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: "6px",
                    fontSize: { xs: "13px", sm: "15px" },
                  }}
                >
                  <AccessTimeIcon fontSize="small" /> {formatTime(timeLeft)}
                </Typography>
                <Button
                  onClick={paused ? handleResume : handlePause}
                  className="icon-button green-hover"
                >
                  {paused ? <PlayArrowIcon /> : <PauseIcon />}
                </Button>
                <Button
                  onClick={handleRetry}
                  className="icon-button green-hover"
                >
                  <RestartAltIcon />
                </Button>
              </Box>
            ) : null}
          </Box>
        </Box>

        {/* Progress */}
        {!gameOver && (
          <LinearProgress
            variant="determinate"
            value={
              (timeLeft / (Number(sessionStorage.getItem("timergame")) || 30)) *
              100
            }
            sx={{
              my: 3,
              height: 10,
              borderRadius: 5,
              backgroundColor: "#eee",
              overflow: "hidden", // Ensures rounded corners show correctly
              "& .MuiLinearProgress-bar": {
                backgroundImage:
                  timeLeft > 10
                    ? "linear-gradient(90deg, #4ade80 , #22c55e )" // Green gradient
                    : timeLeft > 5
                    ? "linear-gradient(90deg, #facc15 , #f97316 )" // Orange to Red
                    : "linear-gradient(90deg,  #f87171, #ef4444 )", // Deep red gradient
                backgroundColor: "transparent", // prevent override
              },
            }}
          />
        )}
        {/* ✅ Show stats if not game over */}
        {!gameOver && (
          <Grid className="scoreBoard">
            {[
              {
                label: "of 20",
                value: problemsAttempted,
                icon: <img src={Target} alt="target" className="stat-icon" />,
              },
              {
                label: "Score",
                value: score,
                icon: <img src={Trophy} alt="target" className="stat-icon" />,
              },
              {
                label: "Streak",
                value: streak,
                icon: <img src={Spark} alt="target" className="stat-icon" />,
              },
              {
                label: "Accuracy",
                value: `${getAccuracy()}%`,
                icon: <img src={Tick} alt="target" className="stat-icon" />,
              },
            ].map((stat, idx) => (
              <Grid item key={idx}>
                <Card className="stat-card">
                  <CardContent
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      padding: "16px",
                    }}
                  >
                    {stat.icon}
                    <Typography fontWeight={700} fontSize={20}>
                      {stat.value}
                    </Typography>
                    <Typography variant="body2">{stat.label}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Game Content */}
        {paused ? (
          <Box
            py={6}
            bgcolor="#f0fdf4"
            sx={{
              display: "flex",
              padding: "34px",
              height: "200px",
              textAlign: "center",
              justifyContent: "center",
              flexDirection: "column",
              alignItems: "center",
              borderRadius: "10px",
              border: "1px solid #e0e0e0",
              boxshadow: " 0 2px 6px rgba(0, 0, 0, 0.05)",
            }}
          >
            <div className="Gamepause">
              <Typography variant="h4" fontSize={"34px"} fontWeight={700}>
                Game Paused
              </Typography>
              <Button
                variant="contained"
                className="ResumeButton"
                onClick={handleResume}
              >
                Resume Game
              </Button>
            </div>
          </Box>
        ) : gameOver ? (
          <Box
            textAlign="center"
            sx={{
              background: "#f9ffff",
              borderRadius: "10px",
              boxShadow: 3,
              padding: "48px",
            }}
          >
            <Typography
              variant="h3"
              fontWeight={700}
              color="primary"
              gutterBottom
              display={"flex"}
              justifyContent={"center"}
              alignContent={"center"}
             
            >
              <img
                src={Trophy}
                alt="Trophy"
                style={{
                  width: "96px",
                  height: "96px",
                  verticalAlign: "middle",
                  marginRight: "8px",
                }}
              />
            </Typography>

            <Typography
              variant="h3"
              fontWeight={700}
              className="ResultTitle"
              gutterBottom
            >
              Training Complete!
            </Typography>

            <Grid container spacing={4} justifyContent="space-around" my={3}>
              <Grid item>
                <Typography fontWeight={700} fontSize={"30px"}>
                  {score}
                </Typography>
                <Typography variant="body2" color={"#4b5563"} fontSize={"14px"}>
                  Final Score
                </Typography>
              </Grid>
              <Grid item>
                <Typography fontWeight={700} fontSize={"30px"}>
                  {score / 12}/20
                </Typography>
                <Typography variant="body2" color={"#4b5563"} fontSize={"14px"}>
                  Correct
                </Typography>
              </Grid>

              <Grid item>
                <Typography fontWeight={700} fontSize={"30px"}>
                  {getAccuracy()}%
                </Typography>
                <Typography variant="body2" color={"#4b5563"} fontSize={"14px"}>
                  Accuracy
                </Typography>
              </Grid>
              <Grid item>
                <Typography fontWeight={700} fontSize={"30px"}>
                  {bestStreak}
                </Typography>
                <Typography variant="body2" color={"#4b5563"} fontSize={"14px"}>
                  Best Streak
                </Typography>
              </Grid>
            </Grid>

            <Grid container justifyContent="center" spacing={2}>
              <Grid item>
                <Button
                  variant="contained"
                  onClick={handleRetry}
                  sx={{
                    backgroundColor: "#0055ff",
                    "&:hover": { backgroundColor: "#0055ff" },
                    fontSize: "15px",
                    gap: "5px",
                    color: "white",
                    width: "250px",
                  }}
                >
                  <RestartAltIcon /> Play Again (Enter)
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  sx={{
                    fontSize: "15px",
                    backgroundColor: "white",
                    "&:hover": {
                      backgroundColor: "#4caf50",
                      color: "white",
                      width: "250px",
                    },
                  }}
                >
                  Back to Skills Trainer
                </Button>
              </Grid>
            </Grid>
          </Box>
        ) : (
          <Box className="math-box" textAlign="center">
            <Typography className="math-problem">
              {currentProblem?.num1}{" "}
              {getOperatorSymbol(currentProblem?.operation)}{" "}
              {currentProblem?.num2} =
              <TextField
                type="number"
                variant="outlined"
                placeholder="?"
                value={answer}
                inputRef={inputRef}
                onChange={handleAnswerChange}
                className="answer-input"
                inputProps={{
                  style: { textAlign: "center" },
                  onKeyDown: (e) => {
                    // Allow "-" for negative values
                    if (["e", "E", "+"].includes(e.key)) {
                      e.preventDefault();
                    }
                  },
                }}
                sx={{
                  "& input[type=number]": {
                    MozAppearance: "textfield",
                  },
                  "& input[type=number]::-webkit-outer-spin-button": {
                    WebkitAppearance: "none",
                    margin: 0,
                  },
                  "& input[type=number]::-webkit-inner-spin-button": {
                    WebkitAppearance: "none",
                    margin: 0,
                  },
                }}
              />
            </Typography>

            <div className="Solutions">
              <Button
                className="submit-btn"
                variant="contained"
                onClick={handleSubmitAnswer}
                disabled={!answer.trim()}
                sx={{ margin: "0 !important" }}
              >
                Submit (Enter)
              </Button>
            </div>
          </Box>
        )}

        {!gameOver && (
          <Box className="shortcuts" textAlign="center" mt={4}>
            <Typography>
              <strong>Shortcuts:</strong> <kbd>Enter</kbd> Submit &nbsp;
              <kbd>Esc</kbd> Pause &nbsp; <kbd>R</kbd> Restart &nbsp;{" "}
              <kbd>0-9</kbd> Number Input
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ArithmeticGame;
